export interface DePdfToolMeta {
  slug: string;
  enSlug: string;
  name: string;
  tagline: string;
  description: string;
  category: "Organisieren & Bearbeiten" | "Optimieren & Reparieren" | "In PDF umwandeln" | "Aus PDF umwandeln" | "Sicherheit & Werkzeuge";
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const DE_PDF_CATEGORIES = [
  "Organisieren & Bearbeiten",
  "Optimieren & Reparieren",
  "In PDF umwandeln",
  "Aus PDF umwandeln",
  "Sicherheit & Werkzeuge",
] as const;

export type DePdfCategory = (typeof DE_PDF_CATEGORIES)[number];

export const DE_PDF_TOOLS: DePdfToolMeta[] = [
  // Organisieren & Bearbeiten
  {
    slug: "pdf-zusammenfuegen",
    enSlug: "merge-pdf",
    name: "PDF zusammenfügen kostenlos",
    tagline: "Kombiniere mehrere PDF-Dateien in einem sauberen Dokument",
    description: "Füge zwei oder mehr PDF-Dokumente zu einer einzigen Datei zusammen. Wähle die Seitenreihenfolge und lade sie in Sekunden datenschutzkonform herunter.",
    category: "Organisieren & Bearbeiten",
    icon: "FilePlus",
    featured: true,
    keywords: ["pdf zusammenfügen", "pdf zusammenführen", "pdf verbinden", "pdf dateien zusammenfügen kostenlos"],
  },
  {
    slug: "pdf-teilen",
    enSlug: "split-pdf",
    name: "PDF teilen & Seiten trennen",
    tagline: "Extrahiere Seiten oder teile deine PDF in einzelne Dateien auf",
    description: "Trenne einzelne Seiten oder extrahiere benutzerdefinierte Seitenbereiche ganz einfach aus deinem PDF-Dokument.",
    category: "Organisieren & Bearbeiten",
    icon: "Scissors",
    featured: true,
    keywords: ["pdf teilen", "pdf seiten trennen", "pdf seiten extrahieren"],
  },
  {
    slug: "pdf-organisieren",
    enSlug: "organize-pdf",
    name: "PDF-Seiten organisieren",
    tagline: "Sortiere, lösche und drehe PDF-Seiten visuell per Drag & Drop",
    description: "Ordne PDF-Seiten visuell neu an, lösche unbenötigte Seiten oder drehe einzelne Blätter.",
    category: "Organisieren & Bearbeiten",
    icon: "LayoutGrid",
    featured: true,
    keywords: ["pdf organisieren", "pdf seiten sortieren", "pdf seiten neu anordnen"],
  },
  {
    slug: "pdf-drehen",
    enSlug: "rotate-pdf",
    name: "PDF drehen online",
    tagline: "Drehe einzelne oder alle Seiten deines PDFs dauerhaft",
    description: "Korrigiere falsch ausgerichtete oder auf dem Kopf stehende PDF-Seiten um 90, 180 oder 270 Grad.",
    category: "Organisieren & Bearbeiten",
    icon: "RotateCw",
    keywords: ["pdf drehen", "pdf seiten drehen", "pdf ausrichtung ändern"],
  },
  {
    slug: "pdf-seiten-extrahieren",
    enSlug: "extract-pages",
    name: "PDF-Seiten extrahieren",
    tagline: "Speichere ausgewählte Seiten in einem neuen PDF-Dokument",
    description: "Wähle bestimmte Seiten aus einer großen PDF-Datei aus und exportiere sie in ein neues sauberes Dokument.",
    category: "Organisieren & Bearbeiten",
    icon: "FileOutput",
    keywords: ["pdf seiten extrahieren", "einzelne pdf seiten speichern"],
  },
  {
    slug: "pdf-seiten-loeschen",
    enSlug: "remove-pages",
    name: "PDF-Seiten löschen",
    tagline: "Entferne leere oder unbenötigte Seiten aus deinem PDF",
    description: "Lösche unerwünschte oder überflüssige Seiten dauerhaft direkt im Browser aus deinen PDF-Dateien.",
    category: "Organisieren & Bearbeiten",
    icon: "Trash2",
    keywords: ["pdf seiten löschen", "pdf seite entfernen", "leere pdf seiten löschen"],
  },
  {
    slug: "pdf-bearbeiten",
    enSlug: "edit-pdf",
    name: "PDF online bearbeiten kostenlos",
    tagline: "Füge Text, Unterschriften, Formen, Markierungen und Bilder hinzu",
    description: "Ergänze Anmerkungen, Textfelder, Freihandzeichnungen, Digitale Unterschriften und Bilder direkt in deinen PDF-Seiten.",
    category: "Organisieren & Bearbeiten",
    icon: "Edit3",
    featured: true,
    keywords: ["pdf bearbeiten", "pdf text hinzufügen", "pdf bearbeiten kostenlos online"],
  },
  {
    slug: "pdf-seitenzahlen",
    enSlug: "page-numbers",
    name: "PDF Seitenzahlen hinzufügen",
    tagline: "Füge benutzerdefinierte Seitenzahlen in Kopf- oder Fußzeile ein",
    description: "Nummeriere deine PDF-Seiten mit anpassbarer Position, Schriftgröße, Formatierung und Rändern.",
    category: "Organisieren & Bearbeiten",
    icon: "Hash",
    keywords: ["pdf seitenzahlen", "seitennummerierung pdf", "pdf seiten nummerieren"],
  },
  {
    slug: "pdf-wasserzeichen",
    enSlug: "watermark-pdf",
    name: "PDF Wasserzeichen hinzufügen",
    tagline: "Füge Text- oder Logo-Wasserzeichen auf deinen PDF-Seiten ein",
    description: "Schütze dein geistiges Eigentum durch das Stempeln von Text- oder Bild-Wasserzeichen auf deinen PDF-Dokumenten.",
    category: "Organisieren & Bearbeiten",
    icon: "Droplets",
    keywords: ["pdf wasserzeichen", "wasserzeichen in pdf einfügen", "pdf mit wasserzeichen schützen"],
  },

  // Optimieren & Reparieren
  {
    slug: "pdf-komprimieren",
    enSlug: "compress-pdf",
    name: "PDF komprimieren & verkleinern",
    tagline: "Reduziere die PDF-Dateigröße ohne spürbaren Qualitätsverlust",
    description: "Komprimiere schwere PDF-Dateien für den schnellen E-Mail-Versand und Online-Uploads bei hoher Schärfe.",
    category: "Optimieren & Reparieren",
    icon: "Archive",
    featured: true,
    keywords: ["pdf komprimieren", "pdf verkleinern", "pdf dateigröße reduzieren"],
  },
  {
    slug: "pdf-graustufen",
    enSlug: "grayscale-pdf",
    name: "PDF in Schwarz-Weiß umwandeln",
    tagline: "Konvertiere farbige PDF-Seiten in Graustufen",
    description: "Wandle farbige PDF-Dokumente in Schwarz-Weiß-Graustufen um, um Drucker-Tinte und Toner zu sparen.",
    category: "Optimieren & Reparieren",
    icon: "SunMedium",
    keywords: ["pdf schwarz weiß umwandeln", "pdf graustufen"],
  },
  {
    slug: "pdf-reparieren",
    enSlug: "repair-pdf",
    name: "Beschädigte PDF reparieren",
    tagline: "Stelle fehlerhafte oder unlesbare PDF-Dateien wieder her",
    description: "Stelle Daten aus beschädigten oder fehlerhaften PDF-Dokumenten direkt in deinem Browser wieder her.",
    category: "Optimieren & Reparieren",
    icon: "Wrench",
    keywords: ["pdf reparieren", "beschädigte pdf reparieren", "pdf datei wiederherstellen"],
  },

  // In PDF umwandeln
  {
    slug: "bild-in-pdf-umwandeln",
    enSlug: "image-to-pdf",
    name: "JPG in PDF umwandeln (Bild zu PDF)",
    tagline: "Wandle JPG, PNG, WEBP und GIF-Bilder in ein PDF um",
    description: "Füge mehrere Fotos und Bilddateien in ein einzelnes formatiertes PDF-Dokument zusammen.",
    category: "In PDF umwandeln",
    icon: "Image",
    featured: true,
    keywords: ["jpg in pdf umwandeln", "bild in pdf umwandeln", "png in pdf umwandeln kostenlos"],
  },
  {
    slug: "word-in-pdf-umwandeln",
    enSlug: "word-to-pdf",
    name: "Word in PDF umwandeln",
    tagline: "Wandle DOCX und DOC Dokumente in PDF um",
    description: "Konvertiere Word-Dokumente in saubere, professionelle PDF-Dateien direkt im Browser.",
    category: "In PDF umwandeln",
    icon: "FileText",
    keywords: ["word in pdf umwandeln", "docx in pdf umwandeln kostenlos"],
  },
  {
    slug: "excel-in-pdf-umwandeln",
    enSlug: "excel-to-pdf",
    name: "Excel in PDF umwandeln",
    tagline: "Konvertiere XLSX Tabellen in PDF Dokumente",
    description: "Wandle Excel-Tabellen in formatierte PDF-Dateien um, die druck- und präsentationsbereit sind.",
    category: "In PDF umwandeln",
    icon: "Table",
    keywords: ["excel in pdf umwandeln", "xlsx in pdf umwandeln"],
  },
  {
    slug: "powerpoint-in-pdf-umwandeln",
    enSlug: "ppt-to-pdf",
    name: "PowerPoint in PDF umwandeln",
    tagline: "Wandle PPTX Präsentationsfolien in ein PDF um",
    description: "Konvertiere PowerPoint-Präsentationen in kompakte PDF-Dokumente.",
    category: "In PDF umwandeln",
    icon: "Presentation",
    keywords: ["powerpoint in pdf umwandeln", "pptx in pdf umwandeln"],
  },
  {
    slug: "html-in-pdf-umwandeln",
    enSlug: "html-to-pdf",
    name: "HTML in PDF umwandeln",
    tagline: "Speichere Webseiten oder HTML-Code als PDF",
    description: "Wandle HTML-Code oder Webseiten-Inhalte in ein sauberes PDF-Dokument um.",
    category: "In PDF umwandeln",
    icon: "Code",
    keywords: ["html in pdf umwandeln", "webseite als pdf speichern"],
  },
  {
    slug: "text-in-pdf-umwandeln",
    enSlug: "txt-to-pdf",
    name: "Text (TXT) in PDF umwandeln",
    tagline: "Wandle einfache Textdateien in PDF-Dokumente um",
    description: "Konvertiere .txt-Dateien in formatierte PDF-Dateien mit anpassbaren Schriftarten.",
    category: "In PDF umwandeln",
    icon: "FileType",
    keywords: ["txt in pdf umwandeln", "text in pdf konvertieren"],
  },

  // Aus PDF umwandeln
  {
    slug: "pdf-in-jpg-umwandeln",
    enSlug: "pdf-to-image",
    name: "PDF in Bild umwandeln (PDF zu JPG)",
    tagline: "Extrahiere PDF-Seiten als JPG- oder PNG-Bilder in hoher Auflösung",
    description: "Konvertiere jede Seite einer PDF-Datei in hochauflösende JPG- oder PNG-Bilder.",
    category: "Aus PDF umwandeln",
    icon: "FileImage",
    featured: true,
    keywords: ["pdf in jpg umwandeln", "pdf in bild umwandeln", "pdf als bild speichern"],
  },
  {
    slug: "pdf-in-word-umwandeln",
    enSlug: "pdf-to-word",
    name: "PDF in Word umwandeln",
    tagline: "Extrahiere Text & wandle PDF in editierbares DOCX um",
    description: "Konvertiere PDF-Dokumente in bearbeitbare Word-Dateien unter Beibehaltung des Textlayouts.",
    category: "Aus PDF umwandeln",
    icon: "FileCode",
    keywords: ["pdf in word umwandeln", "pdf in docx umwandeln bearbeitbar"],
  },
  {
    slug: "pdf-in-excel-umwandeln",
    enSlug: "pdf-to-excel",
    name: "PDF in Excel umwandeln",
    tagline: "Extrahiere Tabellen in XLSX-Tabellendokumente",
    description: "Exportiere Datentabellen aus PDF-Dokumenten in bearbeitbare Excel-Arbeitsblätter.",
    category: "Aus PDF umwandeln",
    icon: "Spreadsheet",
    keywords: ["pdf in excel umwandeln", "pdf tabelle in excel exportieren"],
  },
  {
    slug: "pdf-text-extrahieren",
    enSlug: "pdf-to-text",
    name: "PDF Text extrahieren",
    tagline: "Extrahiere den reinen Text aus PDF-Dateien",
    description: "Hole den gesamten schriftlichen Inhalt aus einer PDF-Datei in einer leichten .txt-Datei heraus.",
    category: "Aus PDF umwandeln",
    icon: "FileLines",
    keywords: ["pdf text extrahieren", "text aus pdf kopieren"],
  },

  // Sicherheit & Werkzeuge
  {
    slug: "pdf-schuetzen",
    enSlug: "protect-pdf",
    name: "PDF mit Passwort schützen",
    tagline: "Verschlüssele und schütze deine PDF mit Passwort",
    description: "Sichere vertrauliche PDF-Dateien mit 128-Bit-Verschlüsselung und individuellem Passwortschutz.",
    category: "Sicherheit & Werkzeuge",
    icon: "Lock",
    featured: true,
    keywords: ["pdf schützen", "pdf passwort schützen", "pdf verschlüsseln"],
  },
  {
    slug: "pdf-entsperren",
    enSlug: "unlock-pdf",
    name: "PDF Passwort entfernen",
    tagline: "Entferne Passwortschutz und Sperren von deinen PDFs",
    description: "Entferne Passwörter und Bearbeitungssperren von PDF-Dokumenten, die dir gehören.",
    category: "Sicherheit & Werkzeuge",
    icon: "Unlock",
    keywords: ["pdf entsperren", "pdf passwort entfernen", "pdf kennwort entfernen"],
  },
  {
    slug: "pdf-unterschreiben",
    enSlug: "sign-pdf",
    name: "PDF online unterschreiben",
    tagline: "Füge digitale Unterschrift oder Freihand-Signatur hinzu",
    description: "Zeichne deine Unterschrift oder lade ein Signatur-Bild hoch, um Verträge und Formulare zu unterzeichnen.",
    category: "Sicherheit & Werkzeuge",
    icon: "PenTool",
    featured: true,
    keywords: ["pdf unterschreiben", "pdf digital signieren", "vertag pdf unterschreiben online"],
  },
  {
    slug: "pdf-ocr-texterkennung",
    enSlug: "ocr-pdf",
    name: "PDF OCR Texterkennung",
    tagline: "Erkenne Text in gescannten PDF-Dokumenten",
    description: "Optische Zeichenerkennung (OCR), um gescannte PDF-Dokumente durchsuchbar zu machen.",
    category: "Sicherheit & Werkzeuge",
    icon: "ScanText",
    keywords: ["pdf ocr", "pdf texterkennung", "scanned pdf to text deutsch"],
  },
  {
    slug: "qr-code-generator",
    enSlug: "qr-code",
    name: "QR Code Generator",
    tagline: "Erstelle individuelle QR-Codes für Links, WLAN, vCards & Text",
    description: "Generiere gestaltete QR-Codes für Websites, WLAN-Netzwerke, Kontaktdaten und Text. Lade sie als PNG herunter oder stemple sie direkt auf PDF-Seiten.",
    category: "Sicherheit & Werkzeuge",
    icon: "QrCode",
    featured: true,
    keywords: ["qr code generator", "qr code erstellen", "wlan qr code", "vcard qr code"],
  },
];

export function getDePdfToolBySlug(slug: string): DePdfToolMeta | undefined {
  return DE_PDF_TOOLS.find((t) => t.slug === slug || t.enSlug === slug);
}

export function getEnPdfSlugForDeSlug(deSlug: string): string {
  const found = DE_PDF_TOOLS.find((t) => t.slug === deSlug || t.enSlug === deSlug);
  return found ? found.enSlug : deSlug;
}

export function getDePdfSlugForEnSlug(enSlug: string): string {
  const found = DE_PDF_TOOLS.find((t) => t.enSlug === enSlug || t.slug === enSlug);
  return found ? found.slug : enSlug;
}

export function relatedDePdfTools(currentSlug: string, count = 4): DePdfToolMeta[] {
  const current = getDePdfToolBySlug(currentSlug);
  return DE_PDF_TOOLS.filter((t) => t.slug !== current?.slug).slice(0, count);
}
